import React from 'react'
import { Box, Typography, makeStyles } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { getEscalationLevels, postEscalationLevels } from './actions'
import { LevelComponent } from './components/escalation-levels-modal/level-component'
import { Button, Modal, PageTitle } from '../root/components/common'
import CustomButton from '../root/components/common/button'
import { cloneDeep, isEqual } from 'lodash'
import { enqueueSnackbar } from '../root/actions'
import { getNewBillingEscalationLevelsEmails } from './api/locations-api'
import { InfoIcon } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import {
    prepareNewEscalationLevel,
    getEscLevelTimeMinsFromList
} from '../../utils'

const useStyles = makeStyles(theme => ({
    root: {
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
    },
    modalTitleWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    pageTitle: {
        textAlign: 'center',
        paddingTop: 10
    },
    levelsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 50,
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 20,
        paddingBottom: 20
    },

    btnWrapper: {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: 20
    },
    buttonText: {
        fontSize: '16',
        fontWeight: 100,
        lineHeight: 1.5,
        fontFamily: theme.typography.fontFamily
    },
    line: {
        width: '100%',
        outline: '1px solid #EAECF0'
    },
    link: theme.link
}))

function LocationEscalationLevels(props) {
    const dispatch = useDispatch()

    const locationId = props.match.params.locationId
    const classes = useStyles()
    const { locale, localeCode } = useSelector(state => state.main)
    const { escalationLevels, location } = useSelector(state => state.locations)
    const [escalationLevelsState, setEscalationLevelsState] = useState(
        escalationLevels
    )
    const [isBillingNotificationOpen, setIsBillingNotificationOpen] = useState(
        false
    )
    const [errors, setErrors] = useState({})

    const addLevelHandler = () => {
        let newLevelNumber = null
        setEscalationLevelsState(prevLevels => {
            const newLevel = prepareNewEscalationLevel(prevLevels, localeCode)
            newLevelNumber = newLevel.level
            return [...prevLevels, newLevel]
        })
        const newErrors = cloneDeep(errors)
        newErrors[newLevelNumber] = generateErrorItem(newLevelNumber)
        setErrors(newErrors)
    }

    const generateErrorItem = levelNumber => {
        return {
            level: levelNumber,
            email: false,
            reportTypes: false,
            message: false
        }
    }

    const onDeleteLevel = level => {
        setEscalationLevelsState(prevLevels => {
            return prevLevels
                .filter(item => level !== item.level)
                .map((item, index) => ({ ...item, level: index + 1 }))
        })
        const newErrors = cloneDeep(errors)
        delete newErrors[level]
        setErrors(newErrors)
    }

    const isLevelValid = escLevel => {
        let isValid = true
        const newErrorsState = cloneDeep(errors)
        if (!escLevel.email || escLevel.email.length === 0) {
            newErrorsState[escLevel.level].email = true
            isValid = false
        } else {
            newErrorsState[escLevel.level].email = false
        }
        if (
            !escLevel.message ||
            (typeof escLevel.message === 'string' &&
                escLevel.message.trim().length === 0)
        ) {
            newErrorsState[escLevel.level].message = true
            isValid = false
        } else {
            newErrorsState[escLevel.level].message = false
        }
        if (!escLevel.reportTypes || escLevel.reportTypes.length === 0) {
            newErrorsState[escLevel.level].reportTypes = true
            isValid = false
        } else {
            newErrorsState[escLevel.level].reportTypes = false
        }
        setErrors(prevErrors => ({
            ...prevErrors,
            [escLevel.level]: newErrorsState[escLevel.level]
        }))
        return isValid
    }

    const onSave = async () => {
        const isValid = escalationLevelsState
            .map(isLevelValid)
            .every(result => result)
        if (isValid) {
            try {
                if (escalationLevelsState.length === 0) {
                    saveEscalationLevels(escalationLevelsState)
                    return
                }
                const newBillingEmails = await getNewBillingEscalationLevelsEmails(
                    {
                        emails: escalationLevelsState.map(item => item.email),
                        customer_id: location.customer_id
                    }
                )
                if (newBillingEmails.length > 0) {
                    setIsBillingNotificationOpen(true)
                } else {
                    saveEscalationLevels(escalationLevelsState)
                }
            } catch (ex) {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            }
        }
    }

    const onConfirmNewBillingEmails = () => {
        setIsBillingNotificationOpen(false)
        saveEscalationLevels(escalationLevelsState)
    }

    const saveEscalationLevels = escalationLevelsState => {
        const escalationLevels = escalationLevelsState.map(item => ({
            email: item.email,
            message: item.message,
            level: item.level,
            locale: item.locale,
            days_until_alert: item.daysUntilAlert,
            hours_until_alert: item.hoursUntilAlert,
            report_types: item.reportTypes
        }))
        dispatch(postEscalationLevels(escalationLevels, locationId))
        return
    }

    useEffect(() => {
        dispatch(getEscalationLevels(locationId))
    }, [locationId])

    useEffect(() => {
        setEscalationLevelsState(escalationLevels)
        if (escalationLevels?.length > 0) {
            const newErrors = escalationLevels.reduce((acc, item) => {
                acc[item.level] = generateErrorItem(item.level)
                return acc
            }, {})
            setErrors(newErrors)
        }
    }, [escalationLevels])

    return (
        <>
            <PageTitle title={locale.locations.escalationLevels} />
            <Typography>{locale.locations.escalationModalSubTitle}</Typography>
            <Box className={classes.levelsWrapper}>
                {escalationLevelsState?.map((item, index, levels) => {
                    const {
                        daysUntilAlertMin,
                        hoursUntilAlertMin
                    } = getEscLevelTimeMinsFromList(levels, index)

                    return (
                        <React.Fragment key={`${item.level}-${index}`}>
                            <LevelComponent
                                reportTypes={item.reportTypes}
                                level={item.level}
                                email={item.email}
                                message={item.message}
                                daysUntilAlert={item.daysUntilAlert}
                                hoursUntilAlert={item.hoursUntilAlert || 0}
                                setEscalationLevelsState={
                                    setEscalationLevelsState
                                }
                                onDeleteLevel={onDeleteLevel}
                                daysUntilAlertMin={daysUntilAlertMin}
                                hoursUntilAlertMin={hoursUntilAlertMin}
                                escalationLevelsState={escalationLevelsState}
                                fullErrorState={errors}
                                errorState={errors[item.level]}
                                setErrorState={setErrors}
                            />
                            <Box className={classes.line} />
                        </React.Fragment>
                    )
                })}
                <Box>
                    <Button
                        variant="text"
                        className={classes.buttonText}
                        onClick={addLevelHandler}
                    >
                        + {locale.locations.addLevel}
                    </Button>
                </Box>
            </Box>
            {!isEqual(escalationLevels, escalationLevelsState) && (
                <Box className={classes.btnWrapper}>
                    <CustomButton style={{ marginRight: 8 }} onClick={onSave}>
                        {locale.common.save}
                    </CustomButton>
                </Box>
            )}
            <Modal
                size="lg"
                onClose={() => setIsBillingNotificationOpen(false)}
                open={isBillingNotificationOpen}
            >
                <div
                    style={{
                        height: 90,
                        width: '100%',
                        padding: 20,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <InfoIcon size={28} style={{ marginRight: 10 }} />
                    {locale.billing.escalationLevelsCreationNotice}
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: 20
                    }}
                >
                    <NavLink
                        className={classes.link}
                        target={'_blank'}
                        to={`/billing`}
                    >
                        {locale.billing.billingDashboard}
                    </NavLink>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Button onClick={onConfirmNewBillingEmails}>
                        {locale.common.acceptTerms}
                    </Button>
                </div>
            </Modal>
        </>
    )
}

export default withRouter(LocationEscalationLevels)
